/**
 * KPI Dashboard Component
 * Comprehensive KPI display with time-based filtering
 * Shows: Status counts, CA metrics, commission averages, payback periods
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { 
  BarChart3, 
  TrendingUp, 
  Clock, 
  CheckCircle2, 
  XCircle, 
  FileText,
  Euro,
  Percent,
  Calendar,
  RefreshCw,
  AlertTriangle,
  Trophy,
  MessageSquare,
  ArrowUpRight,
  ArrowDownRight,
  Loader2
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

// Format currency in EUR
const formatCurrency = (value) => {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(value || 0);
};

// Format percentage
const formatPercent = (value) => {
  return `${((value || 0) * 100).toFixed(1)}%`;
};

// Format currency compact (M€ for millions) — matches dashboard mockup
const formatCompactEur = (value) => {
  const v = Number(value) || 0;
  if (Math.abs(v) >= 1000000) {
    return `${(v / 1000000).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} M€`;
  }
  return formatCurrency(v);
};

// Period options
const PERIODS = [
  { key: 'month', label: 'Mois', labelEn: 'Month' },
  { key: 'quarter', label: 'Trimestre', labelEn: 'Quarter' },
  { key: 'semester', label: 'Semestre', labelEn: 'Semester' },
  { key: 'year', label: 'Année', labelEn: 'Year' },
];

export const KPIDashboard = ({ language = 'fr' }) => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [period, setPeriod] = useState('year');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [kpis, setKpis] = useState({
    en_cours_etude: 0,
    go_count: 0,
    nogo_lost_count: 0,
    contre_proposition_count: 0,
    signed_count: 0,
    total_ca: 0,
    average_ca: 0,
    average_commission_rate: 0,
    average_payback_months: 0,
    average_payback_months_signed: 0,
    total_capex_engaged: 0,
    total_simulations: 0,
    period: '',
    period_start: '',
    period_end: ''
  });

  const fetchKPIs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api().get(`/kpis?period=${period}`);
      setKpis(response.data);
    } catch (err) {
      console.error('Error fetching KPIs:', err);
      setError(language === 'fr' ? 'Erreur lors du chargement des KPIs' : 'Error loading KPIs');
      toast.error(language === 'fr' ? 'Erreur lors du chargement des KPIs' : 'Error loading KPIs');
    } finally {
      setLoading(false);
    }
  }, [api, period, language]);

  useEffect(() => {
    fetchKPIs();
  }, [fetchKPIs]);

  const handlePeriodChange = (newPeriod) => {
    setPeriod(newPeriod);
  };

  const handleRefresh = () => {
    fetchKPIs();
  };

  // Status count cards data
  const statusCards = [
    {
      key: 'en_cours_etude',
      label: language === 'fr' ? 'En cours d\'étude' : 'Under Review',
      value: kpis.en_cours_etude,
      icon: Clock,
      iconBg: 'bg-amber-100',
      iconColor: 'text-amber-600',
      borderColor: 'border-amber-200',
      bgColor: 'bg-amber-50/50',
      valueColor: 'text-amber-600'
    },
    {
      key: 'go_count',
      label: 'GO',
      value: kpis.go_count,
      icon: CheckCircle2,
      iconBg: 'bg-emerald-100',
      iconColor: 'text-emerald-600',
      borderColor: 'border-emerald-200',
      bgColor: 'bg-emerald-50/50',
      valueColor: 'text-emerald-600'
    },
    {
      key: 'nogo_lost',
      label: language === 'fr' ? 'NO GO / Perdus' : 'NO GO / Lost',
      value: kpis.nogo_lost_count,
      icon: XCircle,
      iconBg: 'bg-red-100',
      iconColor: 'text-red-600',
      borderColor: 'border-red-200',
      bgColor: 'bg-red-50/50',
      valueColor: 'text-red-600'
    },
    {
      key: 'contre_proposition',
      label: language === 'fr' ? 'Contre-prop. en cours' : 'Counter-prop. pending',
      value: kpis.contre_proposition_count,
      icon: MessageSquare,
      iconBg: 'bg-orange-100',
      iconColor: 'text-orange-600',
      borderColor: 'border-orange-200',
      bgColor: 'bg-orange-50/50',
      valueColor: 'text-orange-600'
    },
  ];

  // Financial metrics cards
  const financialCards = [
    {
      key: 'total_ca',
      label: language === 'fr' ? 'CA Total (GO)' : 'Total Revenue (GO)',
      value: formatCurrency(kpis.total_ca),
      icon: Euro,
      iconBg: 'bg-emerald-100',
      iconColor: 'text-emerald-600',
      borderColor: 'border-emerald-200',
      bgColor: 'bg-gradient-to-br from-emerald-50 to-white',
      description: `${kpis.go_count} ${language === 'fr' ? 'dossier(s) GO' : 'GO case(s)'}`
    },
    {
      key: 'average_ca',
      label: language === 'fr' ? 'CA Moyen' : 'Average Revenue',
      value: formatCurrency(kpis.average_ca),
      icon: TrendingUp,
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-600',
      borderColor: 'border-blue-200',
      bgColor: 'bg-gradient-to-br from-blue-50 to-white',
      description: language === 'fr' ? 'Par dossier GO' : 'Per GO case'
    },
    {
      key: 'average_commission',
      label: language === 'fr' ? 'Taux Commission Moyen' : 'Avg Commission Rate',
      value: formatPercent(kpis.average_commission_rate),
      icon: Percent,
      iconBg: 'bg-purple-100',
      iconColor: 'text-purple-600',
      borderColor: 'border-purple-200',
      bgColor: 'bg-gradient-to-br from-purple-50 to-white',
      description: language === 'fr' ? 'Sur dossiers GO' : 'On GO cases'
    },
    {
      key: 'average_payback',
      label: language === 'fr' ? 'Payback Moyen' : 'Avg Payback',
      value: `${kpis.average_payback_months.toFixed(1)} ${language === 'fr' ? 'mois' : 'months'}`,
      icon: Calendar,
      iconBg: 'bg-indigo-100',
      iconColor: 'text-indigo-600',
      borderColor: 'border-indigo-200',
      bgColor: 'bg-gradient-to-br from-indigo-50 to-white',
      description: language === 'fr' ? 'Délai de remboursement' : 'Return period'
    },
  ];

  if (error && !loading) {
    return (
      <Card className="border border-red-200 bg-red-50">
        <CardContent className="p-6">
          <div className="flex items-center gap-3 text-red-600">
            <AlertTriangle className="w-6 h-6" />
            <div>
              <p className="font-semibold">{error}</p>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleRefresh}
                className="mt-2 gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                {language === 'fr' ? 'Réessayer' : 'Retry'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const totalDecided = (kpis.go_count || 0) + (kpis.nogo_lost_count || 0) + (kpis.contre_proposition_count || 0) + (kpis.en_cours_etude || 0);
  const goRate = totalDecided ? Math.round((kpis.go_count / totalDecided) * 100) : 0;

  const paybackSigned = kpis.average_payback_months_signed || 0;
  const paybackPct = Math.min(100, Math.round((paybackSigned / 36) * 100));

  return (
    <div className="space-y-3" data-testid="kpi-dashboard">
      {/* Ligne de période : libellé + sélecteur segmenté */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <p className="text-[13px] text-text-3">
          <span className="font-bold uppercase tracking-[0.1em] text-label text-[11px]">Période analysée</span>
          {' · '}{kpis.period || (language === 'fr' ? 'année en cours' : 'current year')}
        </p>
        <div className="flex items-center gap-2">
          <div className="inline-flex items-center gap-0.5 rounded-ctrl bg-segmented p-1">
            {PERIODS.map((p) => (
              <button key={p.key} onClick={() => handlePeriodChange(p.key)}
                className={`rounded-[7px] h-8 px-3 text-[13px] font-semibold transition-colors whitespace-nowrap ${
                  period === p.key ? 'bg-graphite-900 text-white' : 'text-text-2 hover:text-ink'}`}
                data-testid={`kpi-period-${p.key}`}>
                {language === 'fr' ? p.label : p.labelEn}
              </button>
            ))}
          </div>
          <button onClick={handleRefresh} disabled={loading} aria-label="Rafraîchir" className="w-9 h-9 rounded-nav grid place-items-center text-label hover:bg-surface-2 transition-colors" data-testid="kpi-refresh-btn">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* 4 cartes KPI blanches — maquette tableau de bord */}
      {loading ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="kpi-cards">
          {[0, 1, 2, 3].map(i => <Skeleton key={i} className="h-28 w-full" />)}
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="kpi-cards">
          <div className="rounded-card bg-surface p-5" data-testid="kpi-card-ca">
            <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-label mb-2">{language === 'fr' ? 'CA des dossiers GO' : 'GO revenue'}</p>
            <p className="font-display font-semibold text-ink text-[34px] leading-none tabular-nums">{formatCompactEur(kpis.total_ca)}</p>
            <p className="text-[13px] text-text-3 mt-2">{kpis.go_count} {language === 'fr' ? 'dossiers' : 'cases'} · {kpis.period || (language === 'fr' ? 'année' : 'year')}</p>
          </div>
          <div className="rounded-card bg-surface p-5" data-testid="kpi-card-capex">
            <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-label mb-2">{language === 'fr' ? 'CAPEX engagé' : 'CAPEX committed'}</p>
            <p className="font-display font-semibold text-ink text-[34px] leading-none tabular-nums">{formatCompactEur(kpis.total_capex_engaged)}</p>
            <p className="text-[13px] text-text-3 mt-2">{kpis.signed_count} {language === 'fr' ? 'dossiers signés' : 'signed'}</p>
          </div>
          <div className="rounded-card bg-surface p-5" data-testid="kpi-card-taux">
            <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-label mb-2">{language === 'fr' ? 'Taux GO' : 'GO rate'}</p>
            <p className="font-display font-semibold text-ink text-[34px] leading-none tabular-nums">{goRate} %</p>
            <p className="text-[13px] text-text-3 mt-2">{language === 'fr' ? 'Sur' : 'On'} {kpis.total_simulations} {language === 'fr' ? 'simulations' : 'simulations'}</p>
          </div>
          <div className="rounded-card bg-surface p-5" data-testid="kpi-card-payback">
            <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-label mb-2">{language === 'fr' ? 'Payback moyen' : 'Avg payback'}</p>
            <p className="font-display font-semibold text-ink text-[34px] leading-none tabular-nums" data-testid="kpi-payback-signed">
              {paybackSigned.toFixed(1)} <span className="text-[18px] text-text-3 font-normal">{language === 'fr' ? 'mois' : 'mo'}</span>
            </p>
            <div className="mt-3 h-[5px] rounded-[3px] bg-surface-2 overflow-hidden">
              <div className="h-full rounded-[3px] bg-orange" style={{ width: `${paybackPct}%` }} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KPIDashboard;
